import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Globe, ImageOff, Trophy, PauseCircle, PlayCircle, XOctagon, Link2, Copy } from "lucide-react";
import { useLiveRoster } from "../../lib/adminQueues";
import { useDiscovery, publicFileUrl, daysLeft, fetchDiscovery } from "../../lib/publicCampaigns";
import { formatDollars } from "../../lib/draftCampaign";
import { campaignPath } from "../../lib/campaign";
import { BulkConfirmDialog } from "./BulkConfirmDialog";
import axios from "axios";

const client = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("mra_jwt");
  if (t) { cfg.headers = cfg.headers || {}; cfg.headers.Authorization = `Bearer ${t}`; }
  return cfg;
});

/**
 * LiveCampaignsTab — every live/paused campaign with real operations
 * (pause / resume / close early) plus the rule-based homepage lead. The lead
 * is never hand-picked: the three live public campaigns WITH a profile photo
 * closest to their goal.
 */
const Tile = ({ c }) => (
  <div className="flex min-w-0 items-center gap-3">
    {c.cover_file_id ? (
      <img src={publicFileUrl(c.cover_file_id, 240)} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" />
    ) : (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-cobalt font-serif text-xl italic text-sand">{c.first_name.charAt(0)}</div>
    )}
    <div className="min-w-0">
      <p className="truncate font-serif text-lg text-ink">
        {c.first_name} <span className="text-sm not-italic text-olive">· {c.school}</span>
        {c.status === "paused" && <span className="ml-2 rounded-full bg-yellow/40 px-2 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-[0.14em] text-ink" data-testid={`roster-paused-${c.id}`}>Paused</span>}
      </p>
      <p className="truncate text-xs text-olive">
        {c.category} · {formatDollars(c.raised_cents)} of {formatDollars(c.goal_cents)} ({c.pct}%) · {daysLeft(c.ends_at)}d left
        {c.visibility === "private" && <span className="ml-2 inline-flex items-center gap-1 text-scarlet"><EyeOff className="h-3 w-3" /> private</span>}
        {!c.cover_file_id && <span className="ml-2 inline-flex items-center gap-1 text-tangerine" data-testid={`roster-no-photo-${c.id}`}><ImageOff className="h-3 w-3" /> no profile photo</span>}
        {c.slug && <span className="ml-2 inline-flex items-center gap-1 text-cobalt" data-testid={`roster-slug-${c.id}`}><Link2 className="h-3 w-3" /> /{c.slug}</span>}
      </p>
    </div>
  </div>
);

const Gate = () => {
  const { visible, ready, enabled, count, min_required, loading } = useDiscovery();
  if (loading) return null;
  return (
    <div data-testid="curation-gate" data-visible={visible} className={`flex items-start gap-3 rounded-lg border p-4 ${visible ? "border-grass/40 bg-grass/[0.06]" : "border-yellow/60 bg-yellow/[0.15]"}`}>
      <Globe className={`mt-0.5 h-4 w-4 shrink-0 ${visible ? "text-grass" : "text-tangerine"}`} strokeWidth={2} />
      <div className="text-sm text-ink">
        <p className="font-semibold">
          {visible ? "Public discovery is ON." : !enabled ? "Public discovery is switched OFF (CAMPAIGN_BROWSE_ENABLED)." : `Public discovery is hidden — ${count} of ${min_required} live campaigns needed.`}
        </p>
        <p className="mt-0.5 text-olive">
          {visible
            ? `Donors see /campaigns, the "Meet the students" homepage section, and the Campaigns nav link. ${count} live public ${count === 1 ? "campaign" : "campaigns"}.`
            : ready ? "Everything is teed up; flip the switch to show it." : "The nav link keeps pointing at the sample mockups until enough real campaigns are live. It turns on by itself."}
        </p>
      </div>
    </div>
  );
};

const RowActions = ({ c, onPause, onResume, onClose }) => {
  const copy = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}${campaignPath(c)}`); toast.success("Link copied."); } catch { toast.error("Couldn't copy."); }
  };
  return (
    <div className="flex shrink-0 items-center gap-1">
      <button type="button" onClick={copy} aria-label="Copy link" data-testid={`roster-copy-${c.id}`} className="rounded-full p-2 text-olive hover:bg-sand hover:text-ink"><Copy className="h-4 w-4" /></button>
      <Link to={campaignPath(c)} target="_blank" rel="noopener noreferrer" aria-label="View page" className="rounded-full p-2 text-olive hover:bg-sand hover:text-ink"><Eye className="h-4 w-4" /></Link>
      {c.status === "live" ? (
        <button type="button" onClick={onPause} data-testid={`roster-pause-${c.id}`} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink hover:bg-sand"><PauseCircle className="h-3.5 w-3.5" /> Pause</button>
      ) : (
        <button type="button" onClick={onResume} data-testid={`roster-resume-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-sand hover:bg-forest"><PlayCircle className="h-3.5 w-3.5" /> Resume</button>
      )}
      <button type="button" onClick={onClose} data-testid={`roster-close-${c.id}`} className="inline-flex items-center gap-1 rounded-full border border-scarlet/40 px-3 py-1.5 text-xs font-semibold text-scarlet hover:bg-scarlet/8"><XOctagon className="h-3.5 w-3.5" /> Close early</button>
    </div>
  );
};

export const LiveCampaignsTab = () => {
  const { live, loading, error, refresh } = useLiveRoster();
  const [dialog, setDialog] = useState(null); // { kind: 'pause'|'close', c }
  const lead = live.filter((c) => c.leads_homepage);
  const rest = live.filter((c) => !c.leads_homepage);

  const after = async (msg) => { toast.success(msg); await refresh(); await fetchDiscovery({ force: true }); };
  const onResume = async (c) => {
    try { await client.post(`/admin/campaigns/${c.id}/resume`); await after(`${c.first_name}'s campaign is live again.`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't resume."); }
  };
  const onConfirm = async (note) => {
    if (!dialog) return;
    const { kind, c } = dialog;
    try {
      await client.post(`/admin/campaigns/${c.id}/${kind}`, { reason: note });
      await after(kind === "pause" ? `${c.first_name}'s page is paused — hidden from donors until you resume it.` : `${c.first_name}'s campaign is closed. The page stays up at its link.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the campaign.");
    }
    setDialog(null);
  };

  const Row = ({ c, index }) => (
    <li data-testid={`roster-row-${c.id}`} data-status={c.status} className={`flex flex-wrap items-center justify-between gap-4 p-4 ${index != null ? "rounded-lg border-2 border-yellow/70 bg-yellow/[0.12]" : ""}`}>
      <div className="flex min-w-0 items-center gap-4">
        {index != null && <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink font-serif text-lg italic text-sand">{index + 1}</span>}
        <Tile c={c} />
      </div>
      <div className="flex items-center gap-2">
        {index != null && <span className="inline-flex items-center gap-1 rounded-full bg-ink px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-sand"><Trophy className="h-3 w-3" /> {c.pct}%</span>}
        <RowActions c={c} onPause={() => setDialog({ kind: "pause", c })} onResume={() => onResume(c)} onClose={() => setDialog({ kind: "close", c })} />
      </div>
    </li>
  );

  return (
    <div className="space-y-8" data-testid="live-campaigns-tab">
      <Gate />

      <div>
        <h2 className="font-serif text-3xl text-ink">Leading the homepage</h2>
        <p className="mt-1 text-sm text-olive" data-testid="homepage-rule">
          No hand-picking. The three live, public campaigns <strong className="text-ink">with a profile photo</strong> that are closest to their goal lead the homepage section and the top of /campaigns. It recalculates as donations land.
        </p>
        <ol className="mt-5 space-y-3" data-testid="homepage-lead-list">
          {!loading && lead.length === 0 && (
            <li className="rounded-lg border border-dashed border-border bg-bone p-6 text-sm text-olive" data-testid="homepage-lead-empty">
              No live campaign has a profile photo yet, so the homepage section stays hidden.
            </li>
          )}
          {lead.map((c, i) => <Row key={c.id} c={c} index={i} />)}
        </ol>
      </div>

      <div>
        <h3 className="font-serif text-2xl text-ink">All live &amp; paused campaigns <span className="text-base text-olive">({live.length})</span></h3>
        <p className="mt-1 text-sm text-olive">Pause hides a page from donors (they see nothing; the student sees a paused banner). Close ends it early — the page stays up, donations stop, grants already raised keep disbursing. Every action is written to the audit log.</p>
        {loading ? (
          <p className="mt-4 flex items-center gap-2 text-sm text-olive"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
        ) : error ? (
          <p className="mt-4 text-sm text-scarlet">{error}</p>
        ) : rest.length === 0 ? (
          <p className="mt-4 text-sm text-olive" data-testid="homepage-rest-empty">{live.length ? "Every live campaign is leading the homepage." : "No live campaigns yet."}</p>
        ) : (
          <ul className="mt-4 divide-y divide-border rounded-lg border border-border bg-bone" data-testid="homepage-roster">
            {rest.map((c) => <Row key={c.id} c={c} />)}
          </ul>
        )}
      </div>

      <BulkConfirmDialog
        open={!!dialog}
        onOpenChange={(o) => !o && setDialog(null)}
        title={dialog?.kind === "pause" ? "Pause this campaign?" : "Close this campaign early?"}
        description={dialog?.kind === "pause"
          ? "The page disappears from donors until you resume it. The student sees a paused notice on their dashboard. Your note is kept in the audit log."
          : "This ends the campaign now. The page stays up at its link with an 'ended' banner, donations stop, and grants already raised keep going out weekly. Your note is kept in the audit log."}
        itemsSummary={dialog ? [`${dialog.c.first_name} · ${dialog.c.school} · ${formatDollars(dialog.c.raised_cents)} of ${formatDollars(dialog.c.goal_cents)}`] : []}
        confirmLabel={dialog?.kind === "pause" ? "Pause campaign" : "Close campaign"}
        confirmTone={dialog?.kind === "pause" ? "primary" : "danger"}
        requireNote
        notePlaceholder={dialog?.kind === "pause" ? "e.g. Verifying an enrollment document the school flagged." : "e.g. Student withdrew for the semester; funds raised will be disbursed as planned."}
        onConfirm={onConfirm}
      />
    </div>
  );
};
